import type { JupiterSwapQuoteResponse } from "@solomon-labs/swap-client";

export interface JupiterQuoteParams {
    inputMint: string;
    outputMint: string;
    amount: string;
    taker?: string;
}

export type JupiterSwapMode = "ExactIn" | "ExactOut";

export interface JupiterPlatformFee {
    amount?: string;
    feeBps?: number;
}

export type JupiterRoutePlanStep = Record<string, unknown>;

export interface JupiterUltraQuoteResponse extends Omit<JupiterSwapQuoteResponse, "priceImpact"> {
    priceImpact?: number;
    otherAmountThreshold?: string;
    swapMode?: JupiterSwapMode;
    slippageBps?: number;
    taker?: string;
    router?: string;
    routePlan?: JupiterRoutePlanStep[];
    feeMint?: string;
    platformFee?: JupiterPlatformFee;
    signatureFeeLamports?: number;
    signatureFeePayer?: string | null;
    prioritizationFeeLamports?: number;
    prioritizationFeePayer?: string | null;
    rentFeeLamports?: number;
    rentFeePayer?: string | null;
    inUsdValue?: number;
    outUsdValue?: number;
}

export interface JupiterExecuteParams {
    requestId: string;
    transaction: string;
    taker: string;
    expectedInputMint: string;
    expectedOutputMint: string;
    expectedInputAmount: string;
    quotedOutputAmount: string;
    otherAmountThreshold: string;
    swapMode: JupiterSwapMode;
}

type JsonRecord = Record<string, unknown>;

function isJsonRecord(value: unknown): value is JsonRecord {
    return typeof value === "object" && value !== null;
}

function readOptionalNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
}

function readRequiredString(data: JsonRecord, field: string): string {
    const value = data[field];
    if (typeof value === "string") {
        return value;
    }

    throw new Error(`Invalid Jupiter order response: missing ${field}`);
}

function readOptionalString(data: JsonRecord, field: string): string | undefined {
    const value = data[field];
    return typeof value === "string" ? value : undefined;
}

function readOptionalNullableString(data: JsonRecord, field: string): string | null | undefined {
    const value = data[field];
    if (value === null) {
        return null;
    }

    return typeof value === "string" ? value : undefined;
}

function readSwapMode(value: unknown): JupiterSwapMode | undefined {
    return value === "ExactIn" || value === "ExactOut" ? value : undefined;
}

function readRoutePlan(value: unknown): JupiterRoutePlanStep[] | undefined {
    if (!Array.isArray(value) || !value.every(isJsonRecord)) {
        return undefined;
    }

    return value;
}

function readRequiredBigInt(value: string): bigint {
    try {
        return BigInt(value);
    } catch {
        throw new Error("Invalid Jupiter order response");
    }
}

export function parseJupiterUltraQuotePayload(
    payload: unknown,
    options: {
        requestedTaker?: string;
    } = {}
): JupiterUltraQuoteResponse {
    if (!isJsonRecord(payload)) {
        throw new Error("Invalid Jupiter order response");
    }

    const platformFeeRaw = isJsonRecord(payload.platformFee) ? payload.platformFee : undefined;
    const platformFee: JupiterPlatformFee | undefined = platformFeeRaw
        ? {
              amount: typeof platformFeeRaw.amount === "string" ? platformFeeRaw.amount : undefined,
              feeBps: readOptionalNumber(platformFeeRaw.feeBps)
          }
        : undefined;

    return {
        requestId: readRequiredString(payload, "requestId"),
        transaction: typeof payload.transaction === "string" ? payload.transaction : null,
        inputMint: readRequiredString(payload, "inputMint"),
        outputMint: readRequiredString(payload, "outputMint"),
        inAmount: readRequiredString(payload, "inAmount"),
        outAmount: readRequiredString(payload, "outAmount"),
        priceImpact: readOptionalNumber(payload.priceImpact),
        feeBps: readOptionalNumber(payload.feeBps) ?? platformFee?.feeBps ?? 0,
        gasless: payload.gasless === true,
        errorCode: readOptionalNumber(payload.errorCode),
        errorMessage: readOptionalString(payload, "errorMessage"),
        otherAmountThreshold: readOptionalString(payload, "otherAmountThreshold"),
        swapMode: readSwapMode(payload.swapMode),
        slippageBps: readOptionalNumber(payload.slippageBps),
        taker: readOptionalString(payload, "taker") ?? options.requestedTaker,
        router: readOptionalString(payload, "router"),
        routePlan: readRoutePlan(payload.routePlan),
        feeMint: readOptionalString(payload, "feeMint"),
        platformFee,
        signatureFeeLamports: readOptionalNumber(payload.signatureFeeLamports),
        signatureFeePayer: readOptionalNullableString(payload, "signatureFeePayer"),
        prioritizationFeeLamports: readOptionalNumber(payload.prioritizationFeeLamports),
        prioritizationFeePayer: readOptionalNullableString(payload, "prioritizationFeePayer"),
        rentFeeLamports: readOptionalNumber(payload.rentFeeLamports),
        rentFeePayer: readOptionalNullableString(payload, "rentFeePayer"),
        inUsdValue: readOptionalNumber(payload.inUsdValue),
        outUsdValue: readOptionalNumber(payload.outUsdValue)
    };
}

/**
 * Why Jupiter priced a swap but would not build its transaction for the taker.
 * Documented at developers.jup.ag/docs/swap/order-and-execute: `errorCode` is
 * router-specific, so it is only meaningful together with `router`.
 */
export type QuoteRejectionReason =
    | "insufficient_funds"
    | "insufficient_sol"
    | "below_gasless_minimum"
    | "missing_token_account"
    | "unbuildable";

export interface QuoteRejection {
    reason: QuoteRejectionReason;
    router: string | undefined;
    errorCode: number | undefined;
    errorMessage: string | undefined;
}

const AGGREGATOR_ROUTERS = new Set(["metis", "dflow", "okx"]);
const AGGREGATOR_REJECTIONS: Record<number, QuoteRejectionReason> = {
    1: "insufficient_funds",
    2: "insufficient_sol",
    3: "below_gasless_minimum"
};
const JUPITERZ_REJECTIONS: Record<number, QuoteRejectionReason> = {
    1: "insufficient_funds",
    2: "missing_token_account",
    3: "unbuildable"
};

function getRejectionReason(router: string | undefined, errorCode: number | undefined): QuoteRejectionReason {
    if (errorCode === undefined) return "unbuildable";
    if (router === "jupiterz") return JUPITERZ_REJECTIONS[errorCode] ?? "unbuildable";
    if (router && AGGREGATOR_ROUTERS.has(router)) return AGGREGATOR_REJECTIONS[errorCode] ?? "unbuildable";
    return "unbuildable";
}

/**
 * `null` when the quote is executable, or when it is a price-only quote
 * (`transaction: null`, requested without a taker). Otherwise the quote still
 * carries a valid price to show, but must not be signed: Jupiter returns
 * `transaction: ""` plus `errorCode` when it cannot build the swap.
 */
export function getQuoteRejection(quote: JupiterUltraQuoteResponse): QuoteRejection | null {
    if (quote.transaction === null && quote.errorCode === undefined) return null;
    if (quote.transaction && quote.errorCode === undefined) return null;

    return {
        reason: getRejectionReason(quote.router, quote.errorCode),
        router: quote.router,
        errorCode: quote.errorCode,
        errorMessage: quote.errorMessage
    };
}

export function buildJupiterExecuteParams(
    quote: JupiterUltraQuoteResponse | null | undefined,
    request: JupiterQuoteParams | null,
    walletAddress: string | null | undefined
): JupiterExecuteParams | null {
    if (!quote || !request || !walletAddress) {
        return null;
    }

    if (getQuoteRejection(quote)) {
        return null;
    }

    if (
        typeof quote.requestId !== "string" ||
        typeof quote.transaction !== "string" ||
        quote.transaction === "" ||
        typeof quote.otherAmountThreshold !== "string" ||
        typeof quote.outAmount !== "string"
    ) {
        return null;
    }

    if (quote.inputMint !== request.inputMint || quote.outputMint !== request.outputMint) {
        return null;
    }

    if (quote.taker && quote.taker !== walletAddress) {
        return null;
    }

    if (quote.swapMode === "ExactIn") {
        if (quote.inAmount !== request.amount) {
            return null;
        }
    } else if (quote.swapMode === "ExactOut") {
        const requestedInputAmount = readRequiredBigInt(request.amount);
        const thresholdAmount = readRequiredBigInt(quote.otherAmountThreshold);

        if (thresholdAmount > requestedInputAmount) {
            return null;
        }
    } else {
        return null;
    }

    return {
        requestId: quote.requestId,
        transaction: quote.transaction,
        taker: walletAddress,
        expectedInputMint: request.inputMint,
        expectedOutputMint: request.outputMint,
        expectedInputAmount: request.amount,
        quotedOutputAmount: quote.outAmount,
        otherAmountThreshold: quote.otherAmountThreshold,
        swapMode: quote.swapMode
    };
}
